import { Star, MapPin, BadgeCheck, CalendarDays } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Avatar } from "@/components/Avatar";
import { StarRating } from "@/components/StarRating";

const yr = (s) => {
  const d = new Date(s);
  return isNaN(d.getTime()) ? "" : d.getFullYear();
};

export function ProfileView({ profile }) {
  const { t } = useTranslation();
  if (!profile) return <p className="text-center text-slate-400">{t("common.loading")}</p>;
  const r = profile.rating || { avg: 0, count: 0 };
  return (
    <div data-testid="profile-view">
      <div className="flex flex-col items-center text-center">
        {profile.profile_photo ? (
          <img src={profile.profile_photo} alt={profile.name} data-testid="profile-photo" className="w-24 h-24 rounded-full object-cover border border-slate-200" />
        ) : (
          <Avatar name={profile.name} size="lg" accent testid="user-letter-avatar" />
        )}
        <h2 className="font-heading text-2xl font-bold text-slate-900 mt-4">{profile.name}</h2>
        <div className="flex items-center gap-4 mt-2 text-sm text-slate-500 flex-wrap justify-center">
          {profile.host_verified ? (
            <span data-testid="verified-host-badge" className="inline-flex items-center gap-1 bg-green-100 text-green-700 font-bold text-xs px-3 py-1 rounded-full"><BadgeCheck size={14} /> {t("profileView.verifiedHost")}</span>
          ) : profile.is_host && <span className="inline-flex items-center gap-1 text-blue-600 font-medium"><BadgeCheck size={15} /> {t("profileView.host")}</span>}
          {profile.city && <span className="inline-flex items-center gap-1"><MapPin size={14} /> {profile.city}</span>}
          {yr(profile.member_since) && <span className="inline-flex items-center gap-1"><CalendarDays size={14} /> {t("profileView.joined", { year: yr(profile.member_since) })}</span>}
        </div>
        {profile.bio && <p className="text-slate-600 mt-4 max-w-lg leading-relaxed">{profile.bio}</p>}
      </div>

      <div className="flex gap-10 justify-center border-y border-slate-100 py-6 my-8">
        <div className="text-center">
          <div className="flex items-center gap-2 justify-center">
            <span className="font-heading text-3xl font-bold text-slate-900">{r.count ? r.avg.toFixed(1) : "—"}</span>
            <Star size={22} className="fill-amber-400 text-amber-400" />
          </div>
          <p className="text-sm text-slate-500 mt-1">{t("common.reviews", { count: r.count })}</p>
        </div>
      </div>

      <div className="space-y-5 max-w-3xl mx-auto">
        {(!profile.reviews || profile.reviews.length === 0) ? (
          <p className="text-center text-slate-400" data-testid="profile-no-reviews">{t("profileView.noReviews")}</p>
        ) : profile.reviews.map((rev) => (
          <div key={rev.id} data-testid={`review-${rev.id}`} className="bg-white border border-slate-100 rounded-2xl shadow-card p-5">
            <div className="flex items-center gap-3">
              <Avatar name={rev.author_name} size="sm" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-900 text-sm">{rev.author_name}</p>
                <p className="text-xs text-slate-400">{rev.car_name ? `${rev.car_name} · ` : ""}{new Date(rev.created_at).toLocaleDateString()}</p>
              </div>
              <StarRating value={rev.rating} />
            </div>
            {rev.comment && <p className="text-slate-600 text-sm mt-3 leading-relaxed">{rev.comment}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
